"use client";

import { useState } from "react";
import { MapPin } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
interface Location {
  id: number;
  name: string;
  address: string;
  coordinates: {
    x: number;
    y: number;
  };
  image: string;
}
export default function FacilityLocations() {
  const [hoveredLocation, setHoveredLocation] = useState<number | null>(null);
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.15
  });
  const locations: Location[] = [{
    id: 1,
    name: "Port of Spain Facility",
    address: "Industrial Estate, Port of Spain, Trinidad",
    coordinates: {
      x: 35,
      y: 45
    },
    image: "https://calm-primate.10web.cloud/wp-content/uploads/2026/02/Screenshot-2023-02-09-at-6.08.20-PM-scaled.png?v=x2821hiywc"
  }, {
    id: 2,
    name: "Bridgetown Operations",
    address: "Harbour District, Bridgetown, Barbados",
    coordinates: {
      x: 55,
      y: 52
    },
    image: "https://calm-primate.10web.cloud/wp-content/uploads/2026/02/DJI_0034-scaled.jpg?v=9zw6m0e4yc6"
  }, {
    id: 3,
    name: "Kingston Infrastructure Hub",
    address: "Industrial Zone, Kingston, Jamaica",
    coordinates: {
      x: 28,
      y: 38
    },
    image: "https://calm-primate.10web.cloud/wp-content/uploads/2026/02/Screenshot-2023-02-10-at-2.56.15-PM.png?v=1mf5qsw56oc"
  }, {
    id: 4,
    name: "Castries Energy Center",
    address: "Commercial District, Castries, Saint Lucia",
    coordinates: {
      x: 48,
      y: 58
    },
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1545031/img/castries_energy_center.png"
  }];
  return <section id="facility-locations" ref={ref} className="relative min-h-[70vh] overflow-hidden bg-dark-section text-foreground-dark">
      {}
      <div className="absolute inset-0 opacity-20 bg-cover bg-center bg-no-repeat" style={{
      backgroundImage: "url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1545031/img/caribbean_map_texture.png)",
      filter: "grayscale(100%) contrast(0.8)"
    }} />

      {}
      {hoveredLocation !== null && locations.filter(loc => loc.id === hoveredLocation).map(loc => <div key={`marker-${loc.id}`} data-index={loc.id} className="absolute w-4 h-4 bg-accent rounded-full shadow-strong animate-pulse transition-opacity duration-300" style={{
      left: `${loc.coordinates.x}%`,
      top: `${loc.coordinates.y}%`,
      transform: "translate(-50%, -50%)"
    }}>
              <div className="absolute inset-0 bg-accent rounded-full animate-ping opacity-75" />
            </div>)}

      {}
      <div className="relative z-10 container mx-auto px-6 py-16 lg:py-24">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {}
          <div className={`space-y-4 transition-all duration-[var(--duration-slow)] ease-[var(--ease-smooth)] ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <div className="mb-8">
              <h2 className="font-display font-bold text-[clamp(2rem,4.5vw,2.75rem)] leading-[1.3] tracking-tight text-foreground-dark mb-3">
                Facility Locations
              </h2>
              <p className="text-foreground-dark/80 text-lg leading-[1.6] max-w-2xl">
                Our energy infrastructure and digital asset operations span key Caribbean locations,
                strategically positioned for optimal energy efficiency and operational excellence.
              </p>
            </div>

            <div className="space-y-3">
              {locations.map((location, i) => <HoverCard key={location.id} openDelay={200} closeDelay={100}>
                  <HoverCardTrigger asChild>
                    <Card data-index={i} className={`cursor-pointer border border-primary/20 bg-dark-section/80 backdrop-blur-sm 
                        transition-all duration-[var(--duration-medium)] ease-[var(--ease-smooth)]
                        hover:border-primary/40 hover:shadow-strong hover:-translate-y-1.5
                        ${hoveredLocation === location.id ? "border-accent shadow-strong" : ""}
                        ${isVisible ? `opacity-100 translate-y-0 delay-${i * 100}` : "opacity-0 translate-y-4"}`} onMouseEnter={() => setHoveredLocation(location.id)} onMouseLeave={() => setHoveredLocation(null)}>
                      <CardContent className="p-4 flex items-center gap-4">
                        {}
                        <div className="relative w-20 h-20 rounded-md overflow-hidden shrink-0 border border-primary/20">
                          <img src={location.image} alt={location.name} className={`w-full h-full object-cover transition-transform duration-[var(--duration-medium)] ease-[var(--ease-smooth)]
                              ${hoveredLocation === location.id ? "scale-110" : "scale-100"}`} />
                        </div>

                        {}
                        <div className="flex-1 min-w-0">
                          <h3 className="font-display font-semibold text-lg text-foreground-dark mb-1 truncate">
                            {location.name}
                          </h3>
                          <p className="text-sm text-foreground-dark/70 flex items-center gap-1.5">
                            <MapPin className="size-3.5 text-accent shrink-0" />
                            <span className="truncate">{location.address}</span>
                          </p>
                        </div>
                      </CardContent>
                    </Card>
                  </HoverCardTrigger>

                  <HoverCardContent side="right" align="start" className="w-80 bg-dark-section border-primary/30">
                    <div className="space-y-3">
                      <img src={location.image} alt={location.name} className="w-full h-40 object-cover rounded-md" />
                      <div>
                        <h4 className="font-display font-semibold text-foreground-dark mb-1">
                          {location.name}
                        </h4>
                        <p className="text-sm text-foreground-dark/70">{location.address}</p>
                      </div>
                      <p className="text-sm text-foreground-dark/80 leading-relaxed">
                        Strategic facility equipped with advanced energy infrastructure and
                        digital asset operations capabilities.
                      </p>
                    </div>
                  </HoverCardContent>
                </HoverCard>)}
            </div>
          </div>

          {}
          <div className="hidden lg:flex items-center justify-center">
            <div className="relative w-full max-w-lg aspect-square">
              <div className="absolute inset-0 rounded-lg bg-cover bg-center opacity-30 border border-primary/20" style={{
              backgroundImage: "url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1545031/img/caribbean_map_texture.png)"
            }} />
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="text-center space-y-2">
                  <MapPin className="size-16 text-accent mx-auto animate-pulse" />
                  <p className="text-foreground-dark/60 font-display text-sm tracking-wide">
                    Caribbean Operations Network
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}